// TODO: Only IKImage needs 'use client` - separate into another include?
'use client'

/**
 * --------------------------------------------------------------------------
 * This component acts as a wrapper for ImageKit, allowing us to easily
 * apply/change settings globally.
 *
 * Note: IKImage Does not support sizes so picture tag should be used instead
 * --------------------------------------------------------------------------
 **/
import { IKImage } from 'imagekitio-next'

import type { Transformation } from '@/types/imagekit'

import getImagePath from '@/lib/get-image-path'
import { generateImageKitURL } from '@/lib/generate-imagekit-url'

export type BaseAppImageProps = {
  path: string
  alt: string
  height: number | string
  width: number | string
  loading?: 'lazy' | 'eager'
  fetchPriority?: 'auto' | 'high' | 'low'
  transforms: Transformation
  className?: string
  pictureClassName?: string
  imgClassName?: string
  style?: React.CSSProperties
} & extraProps

type extraProps = {
  srcSet?: { [key: string]: { [key: string]: string | number } }
  sizes?: string
  pictureSources?: { [key: string]: { [key: string]: string | number } }
  lqip?: { active: boolean; quality?: number; blur?: number } | null
}

type WithSrcSet = {
  srcSet: { [key: string]: { [key: string]: string | number } }
  sizes: string
  pictureSources?: never
}

type WithPictureSources = {
  pictureSources: { [key: string]: { [key: string]: string | number } }
  srcSet?: never
  sizes?: never
}

type Base1 = BaseAppImageProps & WithSrcSet
type Base2 = BaseAppImageProps & WithPictureSources

const defaultTransforms = {
  focus: 'auto',
  crop: 'maintain_ratio',
  quality: '75',
}

export default function AppImage({
  path,
  alt,
  height,
  width,
  loading = 'lazy',
  fetchPriority = 'auto',
  transforms,
  srcSet,
  sizes,
  pictureSources,
  className = '',
  pictureClassName = '',
  imgClassName = '',
  style = {},
  lqip = { active: true, quality: 20, blur: 3 }, // eslint-disable-line
}: BaseAppImageProps | Base1 | Base2) {
  // Handle images that have been deleted from the media library
  if (!path || typeof path !== 'string') {
    // console.warn('No path provided for image')
    return null
  }

  if (path.includes('svg')) {
    return (
      <img
        src={path}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        fetchPriority={fetchPriority}
        className={`${className} w-full`}
        style={style}
      />
    )
  }

  // Width is required, so inherit from atts if not provided
  if (!transforms?.width) {
    transforms.width = width.toString()
  }

  // Merge the default transforms with the provided transforms
  transforms = {
    ...defaultTransforms,
    ...transforms,
  }

  // Display width and height are used for the aspect ratio class on basic images
  let displayWidth = width
  let displayHeight = height

  // Depending on whether the image is cropped or not, we need to adjust the display width and height
  if (!transforms.crop || transforms.crop === 'maintain_ratio') {
    displayWidth = Number(transforms.width) ?? displayWidth
    displayHeight = Number(transforms.height) ?? displayHeight
  }

  // Srcset method
  if (srcSet) {
    const srcSetTransforms = Object.keys(srcSet).map((size) => {
      // Inherit transforms from the main image and override with srcSet transforms
      const sizeTransforms = {
        ...transforms,
        ...srcSet[size],
      }

      return `${generateImageKitURL(path, sizeTransforms)} ${size}w`
    })

    return (
      <img
        src={generateImageKitURL(path, transforms)}
        alt={alt}
        width={displayWidth}
        height={displayHeight}
        loading={loading}
        fetchPriority={fetchPriority}
        sizes={sizes}
        srcSet={srcSetTransforms.join(', ')}
        className={className}
        style={style}
      />
    )
  }

  // Picture method
  if (pictureSources) {
    return (
      <picture className={pictureClassName} style={style}>
        {Object.keys(pictureSources).map((media) => {
          const sourceTransforms = pictureSources[media]

          return (
            <source
              key={media}
              media={media}
              height={sourceTransforms.height}
              width={sourceTransforms.width}
              srcSet={generateImageKitURL(path, sourceTransforms)}
            />
          )
        })}

        <img
          src={generateImageKitURL(path, transforms)}
          alt={alt}
          width={displayWidth}
          height={displayHeight}
          loading={loading}
          fetchPriority={fetchPriority}
          className={`${className} relative object-cover object-center`} // iOS doesn't update aspect ratio for sources
        />
      </picture>
    )
  }

  // Basic image fallback
  return (
    <>
      <div
        style={{
          ...style,
          aspectRatio: `${displayWidth}/${displayHeight}`,
          position: 'relative',
        }}
        className={className}>
        <IKImage
          urlEndpoint={process.env.NEXT_PUBLIC_IMAGEKIT_URL_ENDPOINT}
          path={getImagePath(path)}
          alt={alt}
          className={imgClassName}
          width={Number(displayWidth)}
          height={Number(displayHeight)}
          priority={loading === 'eager'}
          transformation={[{ ...transforms }]}
          lqip={{ active: false }} // TODO: this needs reviewing as it might be expected to be dynamic
        />
      </div>
    </>
  )
}
